import { PropsWithChildren, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, Checkbox, Popover, tokens } from '@treams/treams-ui';

import Confirm from './Confirm';
import Delete from './Delete';

const Container = styled(Flex)`
	cursor: pointer;
`;

const Chevron = styled(Box)<{ expanded?: boolean }>`
	border-radius: 12px;

	&:hover {
		background: ${tokens.border.subtle};
	}

	svg {
		display: block;
		width: 24px;
		height: 24px;
		transition: 1s cubic-bezier(0, 1, 0, 1) transform;
		${(props) => props.expanded && `transform: rotateZ(90deg);`}

		path {
			fill: ${tokens.text.secondary};
		}
	}
`;

function RowCheckbox(props: {
	focus: boolean;
	onFocus: () => void;
	onBlur: () => void;
	group: graphql.Group;
	hasChildren?: boolean;
	onSelect?: (recursive: boolean) => void;
	onDeselect?: () => void;
	recursive?: boolean;
	single?: boolean;
	canSelect?: boolean;
	selected?: boolean;
	disabled?: boolean;
}) {
	if (props.disabled) {
		return (
			<Container
				style={{
					cursor: 'not-allowed',
				}}
			>
				<Checkbox checked={props.selected} disabled />
			</Container>
		);
	}

	return (
		<Popover.Target
			visible={props.focus}
			contents={
				<>
					{props.onSelect && !props.onDeselect && (
						<Confirm
							single={false}
							canSelect={!!props.canSelect}
							group={props.group}
							hasChildren={props.hasChildren ?? false}
							onSelect={(recursive) => {
								props.onSelect!(recursive);
								props.onBlur();
							}}
						/>
					)}
					{props.onDeselect && (
						<Delete
							group={props.group}
							recursive={props.recursive}
							onSelect={(recursive) => {
								props.onSelect!(recursive);
								props.onBlur();
							}}
							onDeselect={() => {
								props.onDeselect!();
								props.onBlur();
							}}
						/>
					)}
				</>
			}
			onDismiss={() => props.onBlur()}
		>
			<Checkbox
				onClick={() => {
					if (props.single) {
						props.onSelect?.(false);
					} else {
						props.onFocus();
					}
				}}
				checked={props.selected}
			/>
		</Popover.Target>
	);
}

export default function Row(
	props: PropsWithChildren<{
		group: graphql.Group;
		hasChildren?: boolean;
		onSelect?: (recursive: boolean) => void;
		onDeselect?: () => void;
		recursive?: boolean;
		disabled?: boolean;
		single?: boolean;
		selected?: boolean;
		canSelect?: boolean;
	}>,
) {
	const [expanded, setExpanded] = useState(!props.canSelect);
	const [focus, setFocus] = useState(false);

	const onExpandClick = () => {
		if (props.hasChildren && props.children) {
			setExpanded((expanded) => !expanded);
		} else if (props.single) {
			props.onSelect?.(false);
		} else {
			setFocus(true);
		}
	};

	return (
		<>
			<Container p={2} alignItems="center">
				{props.hasChildren && props.children ? (
					<Chevron
						flexGrow={0}
						flexShrink={0}
						mr={2}
						onClick={(event) => {
							setExpanded((expanded) => !expanded);
							event.stopPropagation();
						}}
						expanded={expanded}
					>
						<ChevronRightIcon />
					</Chevron>
				) : (
					<Box width={24} mr={2} />
				)}
				<Box flexGrow={0} flexShrink={0} mr={2}>
					<RowCheckbox
						focus={focus}
						onFocus={() => setFocus(true)}
						onBlur={() => setFocus(false)}
						disabled={props.disabled}
						selected={props.selected}
						group={props.group}
						hasChildren={props.hasChildren}
						onSelect={props.onSelect}
						onDeselect={props.onDeselect}
						recursive={props.recursive}
						single={props.single}
						canSelect={props.canSelect}
					/>
				</Box>
				<Box flexGrow={0} flexShrink={0} mr={2} onClick={onExpandClick}>
					<Avatar.Group size={24} group={props.group} />
				</Box>
				<Box flexGrow={1} flexShrink={1} onClick={onExpandClick}>
					{props.group.name}
				</Box>
			</Container>
			{expanded && props.children}
		</>
	);
}
